import en from "./en.js";
import fr from "./fr.js";

/** Supported application language codes (Electron + displays). */
export type AppLanguage = "fr" | "en";

/** Ultimate fallback when no preference and no browser/OS locale is available. */
export const DEFAULT_APP_LANGUAGE: AppLanguage = "en";

/**
 * Resolve FR/EN from a locale tag. Unknown tags return null (caller decides fallback).
 */
export const parseAppLanguage = (value: unknown): AppLanguage | null => {
  if (typeof value !== "string") {
    return null;
  }
  const lower = value.trim().toLowerCase();
  if (lower === "fr" || lower.startsWith("fr-")) {
    return "fr";
  }
  if (lower === "en" || lower.startsWith("en-")) {
    return "en";
  }
  return null;
};

/**
 * Detect language from the browser / runtime (`navigator.language`).
 * Falls back to English when unavailable or unsupported.
 */
export const detectBrowserLanguage = (): AppLanguage => {
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.language === "string"
  ) {
    return parseAppLanguage(navigator.language) ?? DEFAULT_APP_LANGUAGE;
  }
  return DEFAULT_APP_LANGUAGE;
};

/**
 * Normalize an arbitrary string into a supported AppLanguage.
 * Unknown / missing values fall back to the browser language, then English.
 */
export const normalizeAppLanguage = (value: unknown): AppLanguage => {
  return parseAppLanguage(value) ?? detectBrowserLanguage();
};

let appLanguage: AppLanguage = detectBrowserLanguage();
const languageListeners = new Set<(language: AppLanguage) => void>();

/**
 * Return the active BCP-47 locale tag used by formatters and MUI.
 */
export const getLanguage = (): string => {
  return appLanguage === "fr" ? "fr-FR" : "en-US";
};

/**
 * Return the short language code (`fr` | `en`) currently selected.
 */
export const getAppLanguage = (): AppLanguage => appLanguage;

/**
 * Set the application language and notify subscribers.
 * Used by Electron config, hub broadcast and remote web clients.
 *
 * @param language - Target language code or locale string.
 * @returns The normalized language that was applied.
 */
export const setAppLanguage = (language: unknown): AppLanguage => {
  const next = normalizeAppLanguage(language);
  if (next === appLanguage) {
    return appLanguage;
  }
  appLanguage = next;
  for (const listener of languageListeners) {
    listener(appLanguage);
  }
  return appLanguage;
};

/**
 * Subscribe to language changes. Returns an unsubscribe function.
 *
 * @param listener - Callback invoked with the new AppLanguage.
 * @returns Unsubscribe function.
 */
export const subscribeLanguage = (
  listener: (language: AppLanguage) => void,
): (() => void) => {
  languageListeners.add(listener);
  return () => {
    languageListeners.delete(listener);
  };
};

/**
 * Return the dictionary for the currently selected language.
 */
export default () => {
  if (getAppLanguage() === "fr") {
    return fr;
  }
  return en;
};
